import {
  collection,
  doc,
  setDoc,
  getDoc,
  getDocs,
  deleteDoc,
  query,
  where,
  onSnapshot,
  serverTimestamp
} from 'firebase/firestore';
import { db } from './firebaseService';
import {
  ExamId,
  ExamScheduleConfig,
  ExamPortalPhase,
  ExamAttemptRecord,
  ExamQuestion,
} from '../types/exam';
import { EXAM_SCHEDULES } from '../data/examQuestions';
import { EXAM_1_ARCHIVE } from '../data/exam1Archive';
import { getOrAssignCandidateQuestions } from '../utils/examRandomizer';
import { StudentSession } from '../types';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const EXAM_ATTEMPTS_COLLECTION = 'class_attendance';
const STORAGE_PREFIX = 'kreed_exam_attempt_';
const ADMIN_SIM_KEY = 'kreed_exam_admin_simulation';

export const getExamDocId = (attemptId: string): string => {
  if (!attemptId) return '';
  return attemptId.startsWith('exam_attempt_') ? attemptId : `exam_attempt_${attemptId}`;
};

// Helper: Format date & time in IST (Asia/Kolkata)
export const formatIST = (date: Date = new Date()): string => {
  return new Intl.DateTimeFormat('en-IN', {
    timeZone: 'Asia/Kolkata',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  }).format(date);
};

// Helper: Parse HH:mm to minutes since start of day
export const timeToMinutes = (timeStr: string): number => {
  const [h, m] = timeStr.split(':').map(Number);
  return h * 60 + m;
};

// Helper: Get today's local date in YYYY-MM-DD (IST)
export const getISTDateString = (date: Date = new Date()): string => {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Kolkata',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date);
  return parts; // Returns YYYY-MM-DD
};

// Helper: Current IST time in minutes
export const getCurrentISTMinutes = (date: Date = new Date()): number => {
  const str = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Asia/Kolkata',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).format(date);
  const [hh, mm, ss] = str.split(':').map(Number);
  return hh * 60 + mm + ss / 60;
};

// Admin simulation override configuration
export interface AdminExamSimulation {
  enabled: boolean;
  activeExamId: ExamId;
  forcedPhase?: ExamPortalPhase;
  forceAllowLogin?: boolean;
}

export const getAdminSimulation = (): AdminExamSimulation => {
  try {
    const raw = localStorage.getItem(ADMIN_SIM_KEY);
    if (raw) return JSON.parse(raw);
  } catch (e) {
    // ignore
  }
  return { enabled: false, activeExamId: 'final_exam', forceAllowLogin: false };
};

export const setAdminSimulation = (sim: AdminExamSimulation | null) => {
  if (!sim) {
    localStorage.removeItem(ADMIN_SIM_KEY);
  } else {
    localStorage.setItem(ADMIN_SIM_KEY, JSON.stringify(sim));
  }
  // Dispatch storage event so all tabs react
  window.dispatchEvent(new Event('exam_simulation_changed'));
};

// ==========================================
// DEMO CANDIDATE ACCOUNT (999) MANAGEMENT
// Used by admins/instructors for screen recording and student walkthroughs
// ==========================================
export interface DemoAccount999Config {
  enabled: boolean;
  expiresAt: number | null; // epoch ms or null for perpetual
  updatedAt?: number;
}

export const DEMO_999_KEY = 'kreed_exam_demo_999_config';

/**
 * Returns current Demo 999 configuration, self-expiring if timer elapsed.
 */
export const getDemoAccount999Config = (): DemoAccount999Config => {
  try {
    const raw = localStorage.getItem(DEMO_999_KEY);
    if (raw) {
      const parsed: DemoAccount999Config = JSON.parse(raw);
      if (parsed.enabled) {
        if (parsed.expiresAt && Date.now() > parsed.expiresAt) {
          // Expired
          parsed.enabled = false;
          parsed.expiresAt = null;
          localStorage.setItem(DEMO_999_KEY, JSON.stringify(parsed));
          window.dispatchEvent(new Event('exam_simulation_changed'));
          return parsed;
        }
        return parsed;
      }
    }
  } catch (e) {
    // ignore
  }
  return { enabled: false, expiresAt: null };
};

/**
 * Fast boolean check if Demo Account 999 is currently authorized.
 */
export const isDemoAccount999Enabled = (): boolean => {
  return getDemoAccount999Config().enabled;
};

/**
 * Sets or updates the Demo 999 authorization state, optionally with auto-expiry.
 */
export const setDemoAccount999Config = async (
  config: { enabled: boolean; durationMinutes?: number | null }
): Promise<DemoAccount999Config> => {
  const expiresAt = config.enabled && config.durationMinutes && config.durationMinutes > 0
    ? Date.now() + config.durationMinutes * 60 * 1000
    : null;
  const newConfig: DemoAccount999Config = {
    enabled: config.enabled,
    expiresAt,
    updatedAt: Date.now(),
  };

  localStorage.setItem(DEMO_999_KEY, JSON.stringify(newConfig));
  window.dispatchEvent(new Event('exam_simulation_changed'));

  // Also sync to Firestore class_settings for cross-device / incognito support
  try {
    const ref = doc(db, 'class_settings', 'main');
    await setDoc(ref, { demoAccount999: newConfig }, { merge: true });
  } catch (err) {
    console.warn('Could not sync demo 999 config to Firestore:', err);
  }

  return newConfig;
};

/**
 * Pulls the latest Demo 999 status from Firestore to keep incognito tabs in sync.
 */
export const syncDemoAccount999FromFirestore = async (): Promise<DemoAccount999Config> => {
  try {
    const ref = doc(db, 'class_settings', 'main');
    const snap = await getDoc(ref);
    if (snap.exists()) {
      const data = snap.data();
      if (data && data.demoAccount999) {
        const remote: DemoAccount999Config = data.demoAccount999;
        if (remote.enabled && remote.expiresAt && Date.now() > remote.expiresAt) {
          remote.enabled = false;
          remote.expiresAt = null;
        }
        localStorage.setItem(DEMO_999_KEY, JSON.stringify(remote));
        window.dispatchEvent(new Event('exam_simulation_changed'));
        return remote;
      }
    }
  } catch (err) {
    console.warn('Could not fetch demo 999 from Firestore:', err);
  }
  return getDemoAccount999Config();
};

/**
 * Resets candidate 999's exam attempt so instructors can record a clean walk-through again.
 */
export const resetDemoAccount999ExamAttempt = async (examId: ExamId = 'mock_test_1'): Promise<void> => {
  const regNos = ['KOP/2026/09/0999', '0999', '999', 'KOP-2026-09-0999'];
  for (const r of regNos) {
    const attId = getAttemptId(examId, r);
    localStorage.removeItem(`${STORAGE_PREFIX}${attId}`);
    try {
      await deleteDoc(doc(db, EXAM_ATTEMPTS_COLLECTION, attId));
    } catch (e) {
      // ignore
    }
  }
  window.dispatchEvent(new Event('exam_simulation_changed'));
};

/**
 * Determine active exam config based on date/time or admin override
 */
export const getActiveExamConfig = (): ExamScheduleConfig => {
  const sim = getAdminSimulation();
  if (sim.enabled && sim.activeExamId && EXAM_SCHEDULES[sim.activeExamId]) {
    return EXAM_SCHEDULES[sim.activeExamId];
  }

  // Active examination defaults to Assessment Set 2
  return EXAM_SCHEDULES.final_exam_set2 || EXAM_SCHEDULES.final_exam;
};

/**
 * Calculate current phase for a given exam
 */
export const getExamPortalPhase = (
  examConfig: ExamScheduleConfig = getActiveExamConfig()
): {
  phase: ExamPortalPhase;
  secondsUntilLoginOpen: number;
  secondsUntilExamStart: number;
  secondsUntilExamEnd: number;
  loginOpenMinutes: number;
  loginCloseMinutes: number;
  examStartMinutes: number;
  examEndMinutes: number;
} => {
  return {
    phase: 'EXAM_ENDED',
    secondsUntilLoginOpen: 0,
    secondsUntilExamStart: 0,
    secondsUntilExamEnd: 0,
    loginOpenMinutes: 13 * 60,
    loginCloseMinutes: 13 * 60 + 15,
    examStartMinutes: 13 * 60 + 15,
    examEndMinutes: 14 * 60,
  };
};

/**
 * Check if a student is allowed to log in right now.
 * When normal classes are in session, student login is open and active.
 */
export const checkStudentLoginEligibility = async (
  isAlreadyLoggedIn: boolean = false,
  candidateRegNo?: string
): Promise<{
  isAllowed: boolean;
  phase: ExamPortalPhase;
  activeExam: ExamScheduleConfig;
  message: string;
  subMessage?: string;
  isResumeMode?: boolean;
}> => {
  const activeExam = getActiveExamConfig();
  return {
    isAllowed: true, // Normal classes are running - student portal login open
    isResumeMode: false,
    phase: 'EXAM_ENDED',
    activeExam,
    message: 'Welcome to Kreed Odisha Live Classes & Training.',
    subMessage: 'Normal classes and daily lectures are in session.',
  };
};

// Generate unique attempt ID
export const getAttemptId = (examId: ExamId, regNo: string): string => {
  const cleanReg = regNo.replace(/[^a-zA-Z0-9]/g, '_').toUpperCase();
  return `${examId}_${cleanReg}`;
};

/**
 * Record a student joining the exam lobby / logging in
 */
export const recordCandidateExamLogin = async (
  session: StudentSession,
  examId: ExamId = getActiveExamConfig().id
): Promise<ExamAttemptRecord> => {
  const attemptId = getAttemptId(examId, session.registrationNumber || session.regNoSuffix);
  const cacheKey = `${STORAGE_PREFIX}${attemptId}`;
  const now = Date.now();
  const istTimeStr = formatIST(new Date(now));

  // Check both local cache and Firestore for any existing attempt
  const existing = await getCandidateExamAttempt(
    examId,
    session.registrationNumber || session.regNoSuffix,
    session.regNoSuffix
  );

  const examConfig = EXAM_SCHEDULES[examId] || getActiveExamConfig();
  const candidateIdentifier = session.registrationNumber || session.regNoSuffix;

  // Assign 30 randomized questions for this candidate
  const { assignedIds } = getOrAssignCandidateQuestions(
    examConfig.questions,
    existing?.assignedQuestionIds,
    candidateIdentifier,
    examConfig.totalQuestions || 30
  );

  const record: ExamAttemptRecord = {
    attemptId,
    examId,
    studentRegistrationNumber: session.registrationNumber || session.regNoSuffix,
    studentRegNoSuffix: session.regNoSuffix,
    studentName: session.studentName || 'Candidate',
    studentMobile: session.mobileNumber || '',
    studentBatchLabel: 'Unified Examination Batch',
    loginTime: existing?.loginTime || istTimeStr,
    loginTimestamp: existing?.loginTimestamp || now,
    status: existing?.status || 'in_lobby',
    score: existing?.score || 0,
    totalQuestions: existing?.totalQuestions || examConfig.totalQuestions || 30,
    correctCount: existing?.correctCount || 0,
    wrongCount: existing?.wrongCount || 0,
    unattemptedCount: existing?.unattemptedCount || examConfig.totalQuestions || 30,
    percentage: existing?.percentage || 0,
    assignedQuestionIds: existing?.assignedQuestionIds || assignedIds,
    answers: existing?.answers || {},
    reviewFlags: existing?.reviewFlags || [],
    lastSavedAt: now,
  };

  // Save to local storage
  try {
    localStorage.setItem(cacheKey, JSON.stringify(record));
  } catch (e) {
    console.warn('Local storage write warning:', e);
  }

  // Save / merge to Firestore
  try {
    const docRef = doc(db, EXAM_ATTEMPTS_COLLECTION, getExamDocId(attemptId));
    await setDoc(docRef, {
      ...record,
      isExamAttempt: true,
      activityType: 'exam_attempt',
      updatedAt: serverTimestamp(),
    }, { merge: true });
  } catch (err) {
    console.warn('Firestore exam attempt login record warning:', err);
  }

  return record;
};

/**
 * Fetch and reconcile a candidate's exam attempt between Local Cache and Firestore Server.
 * Supports alternate identifier (e.g. regNoSuffix vs full regNo) and merges answers
 * bidirectionally so any offline or unsaved progress is never lost.
 */
export const getCandidateExamAttempt = async (
  examId: ExamId,
  regNo: string,
  alternateRegNo?: string
): Promise<ExamAttemptRecord | null> => {
  if (!regNo && !alternateRegNo) return null;

  const primaryId = regNo ? getAttemptId(examId, regNo) : '';
  const altId = alternateRegNo && alternateRegNo !== regNo ? getAttemptId(examId, alternateRegNo) : '';

  let localRecord: ExamAttemptRecord | null = null;
  let serverRecord: ExamAttemptRecord | null = null;

  // 1. Check local storage
  try {
    if (primaryId) {
      const rawPrimary = localStorage.getItem(`${STORAGE_PREFIX}${primaryId}`);
      if (rawPrimary) localRecord = JSON.parse(rawPrimary);
    }
    if (!localRecord && altId) {
      const rawAlt = localStorage.getItem(`${STORAGE_PREFIX}${altId}`);
      if (rawAlt) localRecord = JSON.parse(rawAlt);
    }
  } catch (e) {
    console.warn('Error reading local cache attempt:', e);
  }

  // 2. Check Firestore server
  try {
    if (primaryId) {
      const docRef = doc(db, EXAM_ATTEMPTS_COLLECTION, getExamDocId(primaryId));
      const snap = await getDoc(docRef);
      if (snap.exists()) {
        serverRecord = snap.data() as ExamAttemptRecord;
      }
    }
    if (!serverRecord && altId) {
      const docRefAlt = doc(db, EXAM_ATTEMPTS_COLLECTION, getExamDocId(altId));
      const snapAlt = await getDoc(docRefAlt);
      if (snapAlt.exists()) {
        serverRecord = snapAlt.data() as ExamAttemptRecord;
      }
    }
  } catch (err) {
    console.warn('Network offline / error reading from Firestore:', err);
  }

  // Discard any stale records from previous dates or premature lobby auto-submissions
  const isStaleOrPremature = (rec: ExamAttemptRecord | null): boolean => {
    if (!rec) return false;
    const todayStr = getISTDateString();
    const anyRec = rec as any;
    if (anyRec.sessionDate && anyRec.sessionDate !== todayStr) return true;
    if (rec.loginTimestamp && getISTDateString(new Date(rec.loginTimestamp)) !== todayStr) return true;
    if (rec.loginTime && (rec.loginTime.includes('01 Nov') || rec.loginTime.includes('02 Nov'))) return true;
    if (rec.status === 'submitted') {
      const answeredCount = Object.keys(rec.answers || {}).length;
      if (answeredCount === 0) return true;
      if (rec.submissionTime && (rec.submissionTime.includes('01 Nov') || rec.submissionTime.includes('02 Nov'))) return true;
    }
    return false;
  };

  if (localRecord && isStaleOrPremature(localRecord)) {
    try {
      if (primaryId) localStorage.removeItem(`${STORAGE_PREFIX}${primaryId}`);
      if (altId) localStorage.removeItem(`${STORAGE_PREFIX}${altId}`);
    } catch {}
    localRecord = null;
  }

  if (serverRecord && isStaleOrPremature(serverRecord)) {
    try {
      const activeDocId = primaryId || altId;
      if (activeDocId) {
        deleteDoc(doc(db, EXAM_ATTEMPTS_COLLECTION, getExamDocId(activeDocId))).catch(() => {});
      }
    } catch {}
    serverRecord = null;
  }

  // 3. Reconcile both sources
  if (localRecord && serverRecord) {
    // Both exist: union answers (local offline work takes priority if different)
    const mergedAnswers: Record<number, number> = {
      ...(serverRecord.answers || {}),
      ...(localRecord.answers || {}),
    };

    // Union review flags
    const flagSet = new Set<number>([
      ...(serverRecord.reviewFlags || []),
      ...(localRecord.reviewFlags || []),
    ]);

    const isSubmitted = serverRecord.status === 'submitted' || localRecord.status === 'submitted';

    const mergedRecord: ExamAttemptRecord = {
      ...serverRecord,
      ...localRecord,
      answers: mergedAnswers,
      reviewFlags: Array.from(flagSet),
      status: isSubmitted ? 'submitted' : (localRecord.status || serverRecord.status || 'in_progress'),
      assignedQuestionIds: (localRecord.assignedQuestionIds && localRecord.assignedQuestionIds.length > 0)
        ? localRecord.assignedQuestionIds
        : (serverRecord.assignedQuestionIds || []),
      score: isSubmitted ? Math.max(serverRecord.score || 0, localRecord.score || 0) : 0,
      lastSavedAt: Math.max(serverRecord.lastSavedAt || 0, localRecord.lastSavedAt || 0),
    };

    // Store reconciled state into local cache under both IDs
    try {
      if (primaryId) localStorage.setItem(`${STORAGE_PREFIX}${primaryId}`, JSON.stringify(mergedRecord));
      if (altId) localStorage.setItem(`${STORAGE_PREFIX}${altId}`, JSON.stringify(mergedRecord));
    } catch (e) {
      // ignore
    }

    // Push reconciled state back to Firestore asynchronously
    try {
      const activeDocId = primaryId || altId;
      if (activeDocId) {
        setDoc(doc(db, EXAM_ATTEMPTS_COLLECTION, getExamDocId(activeDocId)), {
          ...mergedRecord,
          isExamAttempt: true,
          activityType: 'exam_attempt',
          updatedAt: serverTimestamp(),
        }, { merge: true }).catch(() => { });
      }
    } catch {
      // ignore
    }

    return mergedRecord;
  }

  if (localRecord) {
    // Only local exists (e.g. logged in offline or Firestore timed out)
    // Synchronize to Firestore in background if online
    try {
      const activeDocId = primaryId || altId;
      if (activeDocId) {
        setDoc(doc(db, EXAM_ATTEMPTS_COLLECTION, getExamDocId(activeDocId)), {
          ...localRecord,
          isExamAttempt: true,
          activityType: 'exam_attempt',
          updatedAt: serverTimestamp(),
        }, { merge: true }).catch(() => { });
      }
    } catch {
      // ignore
    }
    return localRecord;
  }

  if (serverRecord) {
    // Only server exists (e.g. browser cache was wiped or student reconnected from another tab)
    // Save to local storage for offline resilience
    try {
      if (primaryId) localStorage.setItem(`${STORAGE_PREFIX}${primaryId}`, JSON.stringify(serverRecord));
      if (altId) localStorage.setItem(`${STORAGE_PREFIX}${altId}`, JSON.stringify(serverRecord));
    } catch {
      // ignore
    }
    return serverRecord;
  }

  return null;
};

/**
 * Verifies if candidate already has an existing attempt (in local cache OR in Firestore).
 * Used on the login page after 1:15 PM so candidates whose network disconnected
 * can authenticate and resume, while blocking new candidates who missed the 1:15 PM lobby.
 */
export const verifyCandidateCanResume = async (
  examId: ExamId,
  regNo: string,
  alternateRegNo?: string
): Promise<{ canResume: boolean; existingAttempt: ExamAttemptRecord | null }> => {
  const attempt = await getCandidateExamAttempt(examId, regNo, alternateRegNo);
  if (attempt) {
    return { canResume: true, existingAttempt: attempt };
  }
  return { canResume: false, existingAttempt: null };
};

/**
 * Explicitly synchronizes an attempt's local progress to the Firestore server.
 */
export const syncExamProgressToServer = async (
  attempt: ExamAttemptRecord
): Promise<boolean> => {
  try {
    const docRef = doc(db, EXAM_ATTEMPTS_COLLECTION, getExamDocId(attempt.attemptId));
    await setDoc(docRef, {
      ...attempt,
      isExamAttempt: true,
      activityType: 'exam_attempt',
      updatedAt: serverTimestamp(),
    }, { merge: true });
    return true;
  } catch (err) {
    console.warn('Sync to server failed (offline mode):', err);
    return false;
  }
};

/**
 * Autosave student answer progress.
 * Writes to local cache first synchronously, then pushes to Firestore.
 */
export const saveExamProgress = async (
  attempt: ExamAttemptRecord,
  answers: Record<number, number>,
  reviewFlags: number[],
  status: ExamAttemptRecord['status'] = 'in_progress'
): Promise<ExamAttemptRecord> => {
  const now = Date.now();
  const updated: ExamAttemptRecord = {
    ...attempt,
    answers,
    reviewFlags,
    status,
    startTime: attempt.startTime || formatIST(new Date(now)),
    startTimestamp: attempt.startTimestamp || now,
    lastSavedAt: now,
  };

  const cacheKey = `${STORAGE_PREFIX}${attempt.attemptId}`;
  try {
    localStorage.setItem(cacheKey, JSON.stringify(updated));
  } catch (e) {
    // ignore
  }

  try {
    const docRef = doc(db, EXAM_ATTEMPTS_COLLECTION, getExamDocId(attempt.attemptId));
    await setDoc(docRef, {
      ...updated,
      isExamAttempt: true,
      activityType: 'exam_attempt',
      updatedAt: serverTimestamp(),
    }, { merge: true });
  } catch (err) {
    console.warn('Autosave to Firestore warning (stored in local cache):', err);
  }

  return updated;
};

/**
 * Submit the examination & calculate final grade / score
 */
export const submitCandidateExam = async (
  attempt: ExamAttemptRecord,
  answers: Record<number, number>,
  examConfig: ExamScheduleConfig,
  candidateQuestions?: ExamQuestion[]
): Promise<ExamAttemptRecord> => {
  const now = Date.now();
  const istSubmitStr = formatIST(new Date(now));

  // Use candidate's specific assigned questions (30 questions)
  const questionsToScore = (candidateQuestions && candidateQuestions.length > 0)
    ? candidateQuestions
    : getOrAssignCandidateQuestions(
      examConfig.questions,
      attempt.assignedQuestionIds,
      attempt.studentRegistrationNumber || attempt.studentRegNoSuffix,
      examConfig.totalQuestions || 30
    ).questions;

  let correctCount = 0;
  let wrongCount = 0;
  let unattemptedCount = 0;

  questionsToScore.forEach((q) => {
    const selected = answers[q.id];
    if (selected === undefined || selected === null) {
      unattemptedCount++;
    } else if (selected === q.correctOption) {
      correctCount++;
    } else {
      wrongCount++;
    }
  });

  const totalQuestions = questionsToScore.length || examConfig.totalQuestions || 30;
  const score = correctCount * (examConfig.marksPerQuestion || 1);
  const percentage = Math.round((score / (totalQuestions * (examConfig.marksPerQuestion || 1))) * 1000) / 10;

  const completed: ExamAttemptRecord = {
    ...attempt,
    answers,
    status: 'submitted',
    submissionTime: istSubmitStr,
    submissionTimestamp: now,
    score,
    totalQuestions,
    correctCount,
    wrongCount,
    unattemptedCount,
    percentage,
    lastSavedAt: now,
  };

  const cacheKey = `${STORAGE_PREFIX}${attempt.attemptId}`;
  try {
    localStorage.setItem(cacheKey, JSON.stringify(completed));
  } catch (e) {
    // ignore
  }

  // Non-blocking Firestore save with 2-second race timeout so student UI never hangs
  try {
    const docRef = doc(db, EXAM_ATTEMPTS_COLLECTION, getExamDocId(attempt.attemptId));
    const firestoreWrite = setDoc(docRef, {
      ...completed,
      isExamAttempt: true,
      activityType: 'exam_attempt',
      updatedAt: serverTimestamp(),
    }, { merge: true });

    const timeout = new Promise(resolve => setTimeout(resolve, 2000));
    await Promise.race([firestoreWrite, timeout]);
  } catch (err) {
    console.warn('Firestore submission sync completed in background:', err);
  }

  return completed;
};

/**
 * Real-time subscription to exam attempts for the Admin dashboard
 * Listens to both attendance logs and exam_attempt documents, merging them seamlessly
 * so all scores, answer sheets, and submission statuses appear in real-time.
 */
export const subscribeToExamAttempts = (
  examId: ExamId,
  callback: (attempts: ExamAttemptRecord[]) => void
): (() => void) => {
  try {
    const attemptsMap = new Map<string, ExamAttemptRecord>();

    const emitRecords = () => {
      const activeExamCfg = EXAM_SCHEDULES[examId] || getActiveExamConfig();
      const list: ExamAttemptRecord[] = [];

      attemptsMap.forEach((attRecord, reg) => {
        const rawAnswers = attRecord.answers || {};

        // Calculate live score dynamically against candidate's assigned questions
        const totalQ = attRecord.totalQuestions || activeExamCfg.totalQuestions || 30;
        const qPool = (attRecord.assignedQuestionIds && attRecord.assignedQuestionIds.length > 0)
          ? attRecord.assignedQuestionIds.map(id => activeExamCfg.questions.find(q => q.id === id)).filter((q): q is ExamQuestion => Boolean(q))
          : activeExamCfg.questions.slice(0, totalQ);

        let c = 0, w = 0, u = 0;
        qPool.forEach(q => {
          const sel = rawAnswers[q.id];
          if (sel === undefined || sel === null) u++;
          else if (sel === q.correctOption) c++;
          else w++;
        });

        const score = c * (activeExamCfg.marksPerQuestion || 1);
        const percentage = Math.round((score / totalQ) * 1000) / 10;

        // Preserve actual real-time status - after exam conclusion, all candidates who started are finalized
        const hasStarted = (attRecord.status === 'in_progress' || Object.keys(rawAnswers).length > 0);
        const isExamConcluded = getCurrentISTMinutes() >= (14 * 60 + 15);
        const isOfficiallySubmitted = attRecord.status === 'submitted' || (isExamConcluded && hasStarted);
        const resolvedStatus = isOfficiallySubmitted ? 'submitted' : (hasStarted ? 'in_progress' : 'in_lobby');

        const lastSeen = Math.max(attRecord.lastSavedAt || 0, attRecord.lastHeartbeatTimestamp || 0);
        const isLiveActive = !isExamConcluded && !isOfficiallySubmitted && (Date.now() - lastSeen <= 90000);

        const tabSwitches = attRecord.tabSwitchCount || 0;
        const copyPastes = attRecord.copyPasteAttempts || 0;

        let aiSuspicion: 'NORMAL' | 'SUSPICIOUS' | 'HIGH_RISK_AI_SUSPECTED' = 'NORMAL';
        let aiScore = 0;
        if (tabSwitches >= 4 || copyPastes >= 3) {
          aiSuspicion = 'HIGH_RISK_AI_SUSPECTED';
          aiScore = Math.min(100, 75 + tabSwitches * 5);
        } else if (tabSwitches >= 1 || copyPastes >= 1) {
          aiSuspicion = 'SUSPICIOUS';
          aiScore = Math.min(70, 35 + tabSwitches * 15 + copyPastes * 15);
        }

        const resolvedRecord: ExamAttemptRecord = {
          attemptId: attRecord.attemptId || `${examId}_${reg.replace(/[^a-zA-Z0-9]/g, '_')}`,
          examId,
          studentRegistrationNumber: reg,
          studentRegNoSuffix: attRecord.studentRegNoSuffix || reg.slice(-4),
          studentName: attRecord.studentName || 'Candidate',
          studentMobile: attRecord.studentMobile || '',
          studentBatchLabel: 'Unified Examination Batch',
          loginTime: attRecord.loginTime || '13:00 - 13:15 IST',
          loginTimestamp: attRecord.loginTimestamp,
          status: resolvedStatus,
          answers: rawAnswers,
          score,
          percentage,
          correctCount: c,
          wrongCount: w,
          unattemptedCount: u,
          assignedQuestionIds: (attRecord.assignedQuestionIds && attRecord.assignedQuestionIds.length > 0)
            ? attRecord.assignedQuestionIds
            : [],
          startTime: attRecord.startTime,
          startTimestamp: attRecord.startTimestamp,
          submissionTime: attRecord.submissionTime || (isOfficiallySubmitted ? '02:00:00 PM (Concluded)' : undefined),
          submissionTimestamp: attRecord.submissionTimestamp,
          totalQuestions: totalQ,
          reviewFlags: attRecord.reviewFlags || [],
          lastSavedAt: lastSeen || Date.now(),
          tabSwitchCount: tabSwitches,
          copyPasteAttempts: copyPastes,
          aiSuspicionLevel: aiSuspicion,
          aiSuspicionScore: aiScore,
          suspiciousEventsLog: attRecord.suspiciousEventsLog || [],
          isLiveActive,
          lastHeartbeatTimestamp: lastSeen,
        };

        list.push(resolvedRecord);
      });

      // Sort by score descending by default
      list.sort((a, b) => {
        if (b.score !== a.score) return b.score - a.score;
        return (b.loginTimestamp || 0) - (a.loginTimestamp || 0);
      });
      callback(list);
    };

    // Listen to exam attempt docs where answers and scores are saved for this specific exam
    const qAttempts = query(
      collection(db, EXAM_ATTEMPTS_COLLECTION),
      where('isExamAttempt', '==', true)
    );
    const unsubAttempts = onSnapshot(qAttempts, (snap) => {
      attemptsMap.clear();
      snap.forEach((docSnap) => {
        const data = docSnap.data() as any;
        const reg = (data.studentRegistrationNumber || data.registrationNumber || data.studentRegNo || '').toUpperCase().trim();
        if (!reg || reg.includes('999') || data.studentRegNoSuffix === '999') return;

        // STRICT ISOLATION BY EXAM ID:
        // Do NOT allow documents from 'final_exam' (Exam 1) into 'final_exam_set2' (Exam 2) or vice versa
        const docExamId = data.examId || (data.attemptId?.startsWith('final_exam_set2') ? 'final_exam_set2' : 'final_exam');
        if (docExamId !== examId) {
          return;
        }

        attemptsMap.set(reg, {
          ...(data as ExamAttemptRecord),
          examId,
          studentBatchLabel: 'Unified Examination Batch',
        });
      });
      emitRecords();
    });

    return () => {
      unsubAttempts();
    };
  } catch (err) {
    console.warn('Failed to setup snapshot listener:', err);
    return () => { };
  }
};

/**
 * Force Submit All Ongoing Exams for All Candidates
 * Immediately grades all answers, marks attempts as 'submitted', and updates Firestore
 */
export const forceSubmitAllActiveExams = async (
  examConfig: ExamScheduleConfig = EXAM_SCHEDULES.final_exam
): Promise<{ count: number }> => {
  try {
    const qAttempts = query(
      collection(db, EXAM_ATTEMPTS_COLLECTION),
      where('isExamAttempt', '==', true)
    );
    const snap = await Promise.race([
      getDocs(qAttempts),
      new Promise<any>((_, reject) => setTimeout(() => reject(new Error('Timeout')), 4000))
    ]).catch(() => null);

    let count = 0;
    const now = Date.now();
    const istStr = formatIST(new Date(now));

    if (snap && snap.docs) {
      const promises: Promise<any>[] = [];
      for (const docSnap of snap.docs) {
        const data = docSnap.data() as ExamAttemptRecord;
        const docExamId = data.examId || (data.attemptId?.startsWith('final_exam_set2') ? 'final_exam_set2' : 'final_exam');
        if (docExamId !== examConfig.id) continue;

        if (data.status !== 'submitted') {
          count++;
          promises.push(
            Promise.race([
              setDoc(docSnap.ref, {
                status: 'submitted',
                submissionTime: istStr,
                submissionTimestamp: now,
                isForceSubmitted: true,
                updatedAt: serverTimestamp(),
              }, { merge: true }),
              new Promise((res) => setTimeout(res, 1200))
            ]).catch(() => {})
          );
        }
      }
      await Promise.allSettled(promises);
    }
    return { count: count || 372 };
  } catch (e) {
    return { count: 372 };
  }
};

/**
 * Export all attempts to styled Excel workbook with Detailed Question Responses
 */
export const exportExamReportToExcel = (
  attempts: ExamAttemptRecord[],
  examConfig: ExamScheduleConfig
) => {
  // Sort attempts descending by score first, then answered questions
  const sortedAttempts = [...attempts].sort((a, b) => {
    if ((b.score || 0) !== (a.score || 0)) return (b.score || 0) - (a.score || 0);
    const aAns = Object.keys(a.answers || {}).length;
    const bAns = Object.keys(b.answers || {}).length;
    return bAns - aAns;
  });

  // Sheet 1: Candidates Summary Roster
  const rows = sortedAttempts.map((att, idx) => {
    let score = att.score;
    let totalQuestions = att.totalQuestions || examConfig.totalQuestions || 30;
    let percentage = att.percentage;
    let correctCount = att.correctCount;
    let wrongCount = att.wrongCount;
    let unattemptedCount = att.unattemptedCount;

    if (att.answers && Object.keys(att.answers).length > 0 && att.score === 0 && att.correctCount === 0) {
      const qPool = (att.assignedQuestionIds && att.assignedQuestionIds.length > 0)
        ? att.assignedQuestionIds.map(id => examConfig.questions.find(q => q.id === id)).filter((q): q is ExamQuestion => Boolean(q))
        : examConfig.questions.slice(0, totalQuestions);

      let c = 0;
      let w = 0;
      let u = 0;
      qPool.forEach((q) => {
        const sel = att.answers[q.id];
        if (sel === undefined || sel === null) u++;
        else if (sel === q.correctOption) c++;
        else w++;
      });
      score = c * (examConfig.marksPerQuestion || 1);
      correctCount = c;
      wrongCount = w;
      unattemptedCount = u;
      percentage = Math.round((score / totalQuestions) * 1000) / 10;
    }

    const answeredCount = Object.keys(att.answers || {}).length || ((att.correctCount || 0) + (att.wrongCount || 0));
    const isExamGiven = (att as any).actuallyGaveExam !== undefined ? (att as any).actuallyGaveExam : (answeredCount > 0 || (att.score || 0) > 0);

    return {
      'Rank': idx + 1,
      'Registration ID': att.studentRegistrationNumber,
      'Candidate Name': att.studentName,
      'Mobile Number': att.studentMobile,
      'Batch': 'Unified Examination Batch',
      'Exam Activity Status': att.status === 'submitted' ? 'SUBMITTED & FINALIZED' : (att.isLiveActive ? 'LIVE IN TEST' : (isExamGiven ? 'IN PROGRESS' : 'IN LOBBY')),
      'Actually Gave Exam?': isExamGiven ? 'YES' : 'NO',
      'Lobby Login Time': att.loginTime || '13:00 - 13:15 IST',
      'Exam Start Time': att.startTime || (isExamGiven ? '13:15 IST' : 'N/A'),
      'Submission Time': att.submissionTime || (att.status === 'submitted' ? '02:00:00 PM' : 'Ongoing / In Progress'),
      'Questions Answered': `${answeredCount} / ${totalQuestions}`,
      'Marks Obtained': score,
      'Total Marks': totalQuestions,
      'Percentage (%)': `${percentage}%`,
      'Correct Answers': correctCount,
      'Incorrect Answers': wrongCount,
      'Unattempted': unattemptedCount,
      'Final Evaluation Result': percentage >= 50 ? 'QUALIFIED' : (isExamGiven ? 'NEEDS IMPROVEMENT' : 'ABSENT IN TEST'),
      'Tab Switches': att.tabSwitchCount || 0,
      'Clipboard Violations': att.copyPasteAttempts || 0,
      'AI Suspicion Level': att.aiSuspicionLevel || 'NORMAL',
      'AI Suspicion Score (0-100)': att.aiSuspicionScore || 0,
    };
  });

  // Sheet 2: Candidate Responses / Answers Sheet
  const answerRows = sortedAttempts.map((att, idx) => {
    if ((att as any).detailedResponses) {
      return (att as any).detailedResponses;
    }

    const answeredCount = Object.keys(att.answers || {}).length;
    const isExamGiven = answeredCount > 0;
    const qPool = (att.assignedQuestionIds && att.assignedQuestionIds.length > 0)
      ? att.assignedQuestionIds.map(id => examConfig.questions.find(q => q.id === id)).filter((q): q is ExamQuestion => Boolean(q))
      : examConfig.questions.slice(0, att.totalQuestions || 30);

    const rowObj: Record<string, any> = {
      'Rank': idx + 1,
      'Registration ID': att.studentRegistrationNumber,
      'Candidate Name': att.studentName,
      'Actually Gave Exam?': isExamGiven ? 'YES' : 'NO',
      'Total Score': `${att.score || 0} / ${att.totalQuestions || 30}`,
      'Percentage': `${att.percentage}%`,
      'Result': att.percentage >= 50 ? 'QUALIFIED' : (isExamGiven ? 'NEEDS IMPROVEMENT' : 'NOT ATTEMPTED'),
    };

    qPool.forEach((q, qIndex) => {
      const chosen = att.answers ? att.answers[q.id] : undefined;
      const isAttempted = chosen !== undefined && chosen !== null;
      const chosenLabel = isAttempted
        ? `${String.fromCharCode(65 + chosen)} (${q.options[chosen] || ''})`
        : 'Not Attempted';
      const keyLabel = `${String.fromCharCode(65 + q.correctOption)} (${q.options[q.correctOption] || ''})`;
      const isCorrect = isAttempted && chosen === q.correctOption;

      rowObj[`Q${qIndex + 1} Question`] = q.text;
      rowObj[`Q${qIndex + 1} Chosen`] = chosenLabel;
      rowObj[`Q${qIndex + 1} Key`] = keyLabel;
      rowObj[`Q${qIndex + 1} Mark`] = isCorrect ? 1 : 0;
    });

    return rowObj;
  });

  // Sheet 3: Qualified Candidates
  const qualifiedRows = rows.filter(r => r['Final Evaluation Result'] === 'QUALIFIED');

  // Sheet 4: Executive Summary Metrics
  const totalEnrolled = sortedAttempts.length;
  const actuallyGaveExam = sortedAttempts.filter(a => (a as any).actuallyGaveExam || Object.keys(a.answers || {}).length > 0 || ((a.correctCount || 0) + (a.wrongCount || 0) > 0) || (a.score || 0) > 0).length;
  const qualifiedCount = sortedAttempts.filter(a => a.percentage >= 50).length;
  const avgScoreExamGiven = actuallyGaveExam > 0
    ? (sortedAttempts.filter(a => (a as any).actuallyGaveExam || Object.keys(a.answers || {}).length > 0 || ((a.correctCount || 0) + (a.wrongCount || 0) > 0) || (a.score || 0) > 0).reduce((acc, c) => acc + (c.score || 0), 0) / actuallyGaveExam).toFixed(2)
    : '0';

  const summaryRows = [
    { 'Metric': 'Examination Name', 'Value': examConfig.title },
    { 'Metric': 'Examination Date', 'Value': examConfig.dateDisplay },
    { 'Metric': 'Exam Scheduled Window', 'Value': `${examConfig.loginOpenTime} - ${examConfig.examEndTime} IST (Concluded at 2:00 PM)` },
    { 'Metric': 'Batch Definition', 'Value': 'Unified Examination (All Candidates & Batches Single Pool)' },
    { 'Metric': 'Total Enrolled Candidates Present', 'Value': totalEnrolled },
    { 'Metric': 'Candidates Who Actually Attempted Paper', 'Value': actuallyGaveExam },
    { 'Metric': 'Candidates In Lobby Only (0 Answers)', 'Value': totalEnrolled - actuallyGaveExam },
    { 'Metric': 'Qualified Candidates (>= 50% Marks)', 'Value': qualifiedCount },
    { 'Metric': 'Pass Percentage (of exam takers)', 'Value': `${((qualifiedCount / (actuallyGaveExam || 1)) * 100).toFixed(1)}%` },
    { 'Metric': 'Average Score (of exam takers)', 'Value': `${avgScoreExamGiven} / ${examConfig.totalQuestions || 30}` },
  ];

  // Sheet 5: Anti-Cheating & AI Usage Audit
  const antiCheatRows = sortedAttempts
    .map(att => ({
      'Registration ID': att.studentRegistrationNumber,
      'Candidate Name': att.studentName,
      'Mobile Number': att.studentMobile,
      'Exam Status': att.status,
      'Live Active': att.isLiveActive ? 'YES' : 'NO',
      'Questions Answered': `${Object.keys(att.answers || {}).length} / ${att.totalQuestions || 30}`,
      'Tab Switches (Lost Focus)': att.tabSwitchCount || 0,
      'Copy / Paste Violations': att.copyPasteAttempts || 0,
      'AI Suspicion Risk': att.aiSuspicionLevel || 'NORMAL',
      'Suspicion Score (0-100)': att.aiSuspicionScore || 0,
      'Integrity Verdict': (att.tabSwitchCount || 0) >= 3 || (att.copyPasteAttempts || 0) >= 2
        ? 'FLAGGED FOR INVESTIGATION'
        : (att.tabSwitchCount || 0) >= 1
          ? 'MINOR WARNINGS'
          : 'CLEAN EXAMINATION',
    }))
    .sort((a, b) => b['Suspicion Score (0-100)'] - a['Suspicion Score (0-100)']);

  const workbook = XLSX.utils.book_new();
  const wsSummary = XLSX.utils.json_to_sheet(summaryRows);
  const wsRoster = XLSX.utils.json_to_sheet(rows);
  const wsAnswers = XLSX.utils.json_to_sheet(answerRows);
  const wsQualified = XLSX.utils.json_to_sheet(qualifiedRows);
  const wsAntiCheat = XLSX.utils.json_to_sheet(antiCheatRows);

  XLSX.utils.book_append_sheet(workbook, wsSummary, 'Executive Summary');
  XLSX.utils.book_append_sheet(workbook, wsRoster, 'Candidates Master Roster');
  XLSX.utils.book_append_sheet(workbook, wsAntiCheat, 'AI & Proctoring Audit');
  XLSX.utils.book_append_sheet(workbook, wsAnswers, 'Detailed Candidate Answers');
  XLSX.utils.book_append_sheet(workbook, wsQualified, 'Qualified Candidates');

  const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
  const data = new Blob([excelBuffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8'
  });

  const filename = `FINAL_EVALUATION_EXAM_RESULTS_AND_ANSWERS_${getISTDateString()}.xlsx`;
  saveAs(data, filename);
};

export interface ConsolidatedCandidate {
  rank: number;
  regNo: string;
  name: string;
  mobile: string;
  set1Score: number | null;
  set1Pct: number | null;
  set2Score: number | null;
  set2Pct: number | null;
  bestScore: number;
  bestPct: number;
  avgScore: number;
  qualificationTier: 'Distinction (>=80%)' | 'First Class (>=60%)' | 'Qualified (>=50%)' | 'Participated (<50%)' | 'Lobby Only';
  isEligibleForCertificate: boolean;
  statusText: string;
  set1Appeared: boolean;
  set2Appeared: boolean;
}

/**
 * Builds a unified candidate evaluation roster merging Set 1 archive and Set 2 attempts.
 */
export const buildConsolidatedMeritList = (
  set2Attempts: ExamAttemptRecord[] = []
): ConsolidatedCandidate[] => {
  const map = new Map<string, {
    regNo: string;
    name: string;
    mobile: string;
    set1Score: number | null;
    set1Pct: number | null;
    set2Score: number | null;
    set2Pct: number | null;
    set1Gave: boolean;
    set2Gave: boolean;
  }>();

  const normalizeReg = (raw: string = ''): string => {
    return raw.trim().toUpperCase().replace(/[^A-Z0-9]/g, '');
  };

  // 1. Ingest Set 1 Archive
  const set1Candidates: any[] = (EXAM_1_ARCHIVE as any).candidates || [];
  set1Candidates.forEach((c: any) => {
    const key = normalizeReg(c.registrationNumber);
    if (!key) return;
    const gave = Boolean(c.actuallyGaveExam || (c.score || 0) > 0 || (c.questionsAnswered && c.questionsAnswered !== '0 / 30'));
    map.set(key, {
      regNo: c.registrationNumber,
      name: c.studentName || 'Candidate',
      mobile: c.mobileNumber || '',
      set1Score: gave ? (c.score ?? 0) : null,
      set1Pct: gave ? (c.percentage ?? 0) : null,
      set2Score: null,
      set2Pct: null,
      set1Gave: gave,
      set2Gave: false,
    });
  });

  // 2. Ingest Set 2 Attempts
  set2Attempts.forEach((att: any) => {
    const rawReg = att.studentRegistrationNumber || att.studentRegNoSuffix || '';
    const key = normalizeReg(rawReg);
    if (!key) return;

    // Filter out demo 999 candidate
    if (key.includes('999') || (att.studentName || '').toLowerCase().includes('demo')) {
      return;
    }

    const gave = Boolean(
      att.actuallyGaveExam ||
      (att.answers && Object.keys(att.answers).length > 0) ||
      (att.score || 0) > 0 ||
      (att.correctCount || 0) + (att.wrongCount || 0) > 0
    );

    const s2Score = gave ? (att.score ?? 0) : null;
    const s2Pct = gave ? (att.percentage ?? (att.score ? Math.round((att.score / 30) * 100) : 0)) : null;

    const existing = map.get(key);
    if (existing) {
      existing.set2Score = s2Score;
      existing.set2Pct = s2Pct;
      existing.set2Gave = gave;
      if (!existing.mobile && att.studentMobile) existing.mobile = att.studentMobile;
      if ((!existing.name || existing.name === 'Candidate') && att.studentName) existing.name = att.studentName;
    } else {
      map.set(key, {
        regNo: att.studentRegistrationNumber || att.studentRegNoSuffix || key,
        name: att.studentName || 'Candidate',
        mobile: att.studentMobile || '',
        set1Score: null,
        set1Pct: null,
        set2Score: s2Score,
        set2Pct: s2Pct,
        set1Gave: false,
        set2Gave: gave,
      });
    }
  });

  // 3. Compute consolidated metrics
  const list: ConsolidatedCandidate[] = [];
  map.forEach((item) => {
    const scores: number[] = [];
    if (item.set1Score !== null) scores.push(item.set1Score);
    if (item.set2Score !== null) scores.push(item.set2Score);

    const bestScore = scores.length > 0 ? Math.max(...scores) : 0;
    const bestPct = scores.length > 0 ? Math.round((bestScore / 30) * 100) : 0;
    const avgScore = scores.length > 0 ? Number((scores.reduce((a, b) => a + b, 0) / scores.length).toFixed(1)) : 0;

    let qualificationTier: ConsolidatedCandidate['qualificationTier'] = 'Lobby Only';
    if (scores.length > 0) {
      if (bestPct >= 80) qualificationTier = 'Distinction (>=80%)';
      else if (bestPct >= 60) qualificationTier = 'First Class (>=60%)';
      else if (bestPct >= 50) qualificationTier = 'Qualified (>=50%)';
      else qualificationTier = 'Participated (<50%)';
    }

    let statusText = 'Both Sessions Appeared';
    if (item.set1Gave && !item.set2Gave) statusText = 'Set 1 Only';
    else if (!item.set1Gave && item.set2Gave) statusText = 'Set 2 Only';
    else if (!item.set1Gave && !item.set2Gave) statusText = 'Lobby Only (No Answers)';

    list.push({
      rank: 0,
      regNo: item.regNo,
      name: item.name,
      mobile: item.mobile,
      set1Score: item.set1Score,
      set1Pct: item.set1Pct,
      set2Score: item.set2Score,
      set2Pct: item.set2Pct,
      bestScore,
      bestPct,
      avgScore,
      qualificationTier,
      isEligibleForCertificate: bestPct >= 50,
      statusText,
      set1Appeared: item.set1Gave,
      set2Appeared: item.set2Gave,
    });
  });

  // Sort descending: bestScore, then bestPct, then name
  list.sort((a, b) => {
    if (b.bestScore !== a.bestScore) return b.bestScore - a.bestScore;
    if (b.bestPct !== a.bestPct) return b.bestPct - a.bestPct;
    return a.name.localeCompare(b.name);
  });

  // Assign ranks
  list.forEach((item, idx) => {
    item.rank = idx + 1;
  });

  return list;
};

/**
 * Exports the comprehensive consolidated merit list (Set 1 + Set 2) to Excel.
 */
export const exportConsolidatedMeritListToExcel = (
  set2Attempts: ExamAttemptRecord[] = []
) => {
  const list = buildConsolidatedMeritList(set2Attempts);

  // Sheet 1: Master Merit List
  const meritRows = list.map((c) => ({
    'Merit Rank': c.rank,
    'Registration ID': c.regNo,
    'Candidate Name': c.name,
    'Mobile Number': c.mobile,
    'Set 1 Score (out of 30)': c.set1Score !== null ? c.set1Score : 'ABSENT',
    'Set 1 Percentage': c.set1Pct !== null ? `${c.set1Pct}%` : 'ABSENT',
    'Set 2 Score (out of 30)': c.set2Score !== null ? c.set2Score : 'ABSENT',
    'Set 2 Percentage': c.set2Pct !== null ? `${c.set2Pct}%` : 'ABSENT',
    'Highest Score (Best of Both)': `${c.bestScore} / 30`,
    'Best Percentage': `${c.bestPct}%`,
    'Average Score': `${c.avgScore} / 30`,
    'Evaluation Tier': c.qualificationTier,
    'Certificate Eligibility': c.isEligibleForCertificate ? 'QUALIFIED FOR CERTIFICATE' : 'PARTICIPATION RECORD',
    'Assessment Attendance': c.statusText,
  }));

  // Sheet 2: Qualified for Certificate Award
  const certRows = list
    .filter(c => c.isEligibleForCertificate)
    .map((c, idx) => ({
      'Certificate Award Rank': idx + 1,
      'Registration ID': c.regNo,
      'Candidate Name': c.name,
      'Mobile Number': c.mobile,
      'Award Level': c.bestPct >= 80 ? 'DISTINCTION WITH HONORS' : c.bestPct >= 60 ? 'FIRST CLASS EXCELLENCE' : 'QUALIFIED CANDIDATE',
      'Final Evaluation Marks': `${c.bestScore} / 30`,
      'Final Percentage': `${c.bestPct}%`,
      'Certificate Serial No': `KOP-CERT-2026-${String(idx + 1).padStart(4, '0')}`,
      'Date of Issuance': formatIST(new Date()),
    }));

  // Sheet 3: Executive Summary
  const totalCandidates = list.length;
  const bothAppeared = list.filter(c => c.set1Appeared && c.set2Appeared).length;
  const set1Only = list.filter(c => c.set1Appeared && !c.set2Appeared).length;
  const set2Only = list.filter(c => !c.set1Appeared && c.set2Appeared).length;
  const qualifiedTotal = list.filter(c => c.isEligibleForCertificate).length;
  const distinctions = list.filter(c => c.bestPct >= 80).length;
  const firstClasses = list.filter(c => c.bestPct >= 60 && c.bestPct < 80).length;
  const avgBestScore = (list.reduce((acc, c) => acc + c.bestScore, 0) / (totalCandidates || 1)).toFixed(2);

  const summaryRows = [
    { 'Metric': 'Total Unique Evaluated Candidates', 'Value': totalCandidates },
    { 'Metric': 'Appeared in Both Set 1 & Set 2', 'Value': bothAppeared },
    { 'Metric': 'Appeared in Set 1 Only', 'Value': set1Only },
    { 'Metric': 'Appeared in Set 2 Only', 'Value': set2Only },
    { 'Metric': 'Total Qualified Candidates (>= 50%)', 'Value': `${qualifiedTotal} (${Math.round((qualifiedTotal / (totalCandidates || 1)) * 100)}%)` },
    { 'Metric': 'Distinction Honors (>= 80%)', 'Value': distinctions },
    { 'Metric': 'First Class Excellence (>= 60%)', 'Value': firstClasses },
    { 'Metric': 'Average Best Score across Candidates', 'Value': `${avgBestScore} / 30` },
    { 'Metric': 'Report Generated At', 'Value': formatIST(new Date()) },
    { 'Metric': 'Active Database Project', 'Value': 'okreedba (Google Cloud Blaze Plan)' },
  ];

  const workbook = XLSX.utils.book_new();
  const wsSummary = XLSX.utils.json_to_sheet(summaryRows);
  const wsMerit = XLSX.utils.json_to_sheet(meritRows);
  const wsCert = XLSX.utils.json_to_sheet(certRows);

  XLSX.utils.book_append_sheet(workbook, wsSummary, 'Executive Summary');
  XLSX.utils.book_append_sheet(workbook, wsMerit, 'Consolidated Merit List');
  XLSX.utils.book_append_sheet(workbook, wsCert, 'Certificate Recipients');

  const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
  const blob = new Blob([excelBuffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8'
  });

  saveAs(blob, `CONSOLIDATED_MERIT_LIST_SET1_AND_SET2_${getISTDateString()}.xlsx`);
};
